import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import type { OverviewDeal, DealStatusKind } from "@/hooks/useDashboardOverview";
import { StageTrack } from "./StageTrack";
import { SERIES_BG, TRACK_BG } from "./opsColors";

const DOT: Record<DealStatusKind, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-600", label: "Active" },
  quiet: { cls: "bg-amber-500", label: "Quiet" },
  stalled: { cls: "bg-red-500", label: "Stalled" },
  no_milestones: { cls: "bg-muted-foreground/40", label: "No milestones" },
  override: { cls: "bg-sky-500", label: "Status set by hand" },
};
const FUNNEL: [keyof OverviewDeal["funnel"], string][] = [["teasers", "T"], ["ndas", "N"], ["cims", "C"], ["iois", "I"], ["lois", "L"]];

export function ActiveDealsWidget({ deals }: { deals: OverviewDeal[] }) {
  return (
    <WidgetPanel title="Active deals" meta={deals.length} action={<Link href="/clients" className="text-xs font-medium text-primary-text hover:underline">Clients</Link>}>
      {deals.length === 0 ? (
        <p className="px-3 py-4 text-sm text-muted-foreground">No active deals yet.</p>
      ) : deals.map((d) => {
        const dot = DOT[d.statusKind] ?? DOT.active;
        const nm = d.nextMilestone;
        return (
          <div key={d.client.id} className="px-3 py-2 border-b border-border/40 hover:bg-muted/40">
            <div className="flex items-baseline gap-2 text-[0.8125rem] min-w-0">
              <span className={cn("w-2 h-2 rounded-full shrink-0 self-center", dot.cls)} title={dot.label} />
              <span className="font-semibold whitespace-nowrap">{d.projectName ?? d.client.name}</span>
              <Link href={`/clients/${d.client.id}`} className="text-primary-text truncate min-w-0 hover:underline">{d.client.name}</Link>
              {d.daysLive !== null && <span className="ml-auto text-xs text-muted-foreground tabular-nums whitespace-nowrap">{`${d.daysLive}d live`}</span>}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <StageTrack stage={d.stage} />
              {d.milestonesTotal > 0 && (
                <span className="flex items-center gap-1.5 flex-1 min-w-0" title={`${d.milestonesDone} of ${d.milestonesTotal} milestones complete`}>
                  <span className={cn("flex-1 h-1 rounded-full overflow-hidden", TRACK_BG)}>
                    <span className={cn("block h-full", SERIES_BG)} style={{ width: `${(d.milestonesDone / d.milestonesTotal) * 100}%` }} />
                  </span>
                  <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{`${d.milestonesDone}/${d.milestonesTotal} milestones`}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground min-w-0">
              <span className="truncate flex-1 min-w-0">
                {nm ? <>{`Next: ${nm.name}`}{nm.dueInDays !== null && (nm.overdue
                  ? <> · <span className="font-semibold text-red-600">{`${-nm.dueInDays}d overdue`}</span></>
                  : ` · in ${nm.dueInDays}d`)}</> : "No open milestones"}
              </span>
              {d.funnel.teasers > 0 ? (
                <span className="flex gap-0.5 shrink-0" title="Teaser · NDA · CIM · IOI · LOI">
                  {FUNNEL.map(([k, l]) => (
                    <span key={k} className={cn("px-1 rounded text-[11px] bg-muted", k === "iois" && d.funnel.iois > 0 && "bg-amber-100 dark:bg-amber-500/20")}>
                      {l}<b className="ml-0.5 text-foreground tabular-nums">{d.funnel[k].toLocaleString()}</b>
                    </span>
                  ))}
                </span>
              ) : <span className="text-[11px] shrink-0">no buyers yet</span>}
            </div>
          </div>
        );
      })}
    </WidgetPanel>
  );
}
